// GET /diplomas/landing-summaries keyed by diploma id, with the "new leads"
// count per diploma (since this user last opened that diploma's Leads tab, or
// the last 30 days) and the plain 30-day count. The request sends the oldest of
// those times as leadsSince (and leadsSinceById for a per-id count); a diploma
// whose own times are later is recounted from its lead list, which only runs
// for diplomas that have leads in the window. A failure leaves the map empty
// (the list still works).
import { useCallback, useEffect, useState } from 'react';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import type { LandingSummary } from 'src/@types/landing/landing.contract';
import { getLeadsSeenMap, leadsWindowStart } from './rules/labels';
import { fetchLeads } from './rules/leadsCsv';

// leadsSince = new since the last visit (per diploma); leads30Days = all leads
// of the last 30 days.
export type LandingSummaryRow = LandingSummary & { leads30Days?: number };

const after = (iso: string) => {
  const t = Date.parse(iso);
  return (l: { createdAt?: string }) => Date.parse(l.createdAt || '') > t;
};

export default function useLandingSummaries(enabled = true) {
  const [items, setItems] = useState<LandingSummaryRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const windowStart = leadsWindowStart();
    const seen = getLeadsSeenMap();
    const since = Object.keys(seen).reduce(
      (min, id) => (Date.parse(seen[id]) < Date.parse(min) ? seen[id] : min),
      windowStart
    );
    const byId = Object.keys(seen)
      .map((id) => `${id}:${seen[id]}`)
      .join(',');
    const url = `${APIS.DIPLOMAS.LANDING_SUMMARIES}?leadsSince=${encodeURIComponent(since)}${
      byId ? `&leadsSinceById=${encodeURIComponent(byId)}` : ''
    }`;
    try {
      const res = await axiosInstance.get(url);
      const body = res.data?.data ?? res.data;
      const list: LandingSummary[] = Array.isArray(body?.items)
        ? body.items
        : Array.isArray(body)
        ? body
        : [];
      const rows = await Promise.all(
        list.map(async (s): Promise<LandingSummaryRow> => {
          const own = seen[s._id] || windowStart;
          // Nothing since the oldest time, or both times are that oldest time: the count stands.
          if (!s.leadsSince || (own === since && windowStart === since)) {
            return { ...s, leads30Days: s.leadsSince || 0 };
          }
          try {
            const leads = await fetchLeads(s._id);
            return {
              ...s,
              leadsSince: leads.filter(after(own)).length,
              leads30Days: leads.filter(after(windowStart)).length,
            };
          } catch {
            return { ...s, leads30Days: undefined };
          }
        })
      );
      setItems(rows);
      setError('');
    } catch (err: any) {
      setItems([]);
      setError(err?.message || 'Could not load the landing pages');
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  const byId: Record<string, LandingSummaryRow> = {};
  items.forEach((s) => {
    byId[s._id] = s;
  });

  return { items, byId, loaded, error, reload: load };
}
